// So sánh 2 version pack theo SemVer 2.0.0 §11 để phát hiện HẠ CẤP (ticket #191).
//
// Background:
//   `avatar sync` trần luôn kéo bản stable mới nhất. Máy đang cài bản beta MỚI HƠN
//   (vd v1.1.41-beta.0) chạy `avatar sync` sẽ bị đưa về stable cũ hơn (v1.1.40) mà
//   KHÔNG có dấu hiệu nào. Module này chỉ PHÂN LOẠI chuyển version; caller
//   (`src/commands/sync.ts`) quyết định in cảnh báo — hành vi mặc định không đổi.
//
// Vì sao không dùng `pickLatestStableSemVerTag`:
//   Hàm đó sort theo [major, minor, patch] và BỎ QUA prerelease
//   (`pick-latest-stable-semver-tag.ts:62-66`) ⇒ `v1.1.38-beta.0` và `v1.1.38` ra
//   "bằng nhau". Sai với §11. Ở đây chỉ dùng lại `parseSemVerTag` cho khâu PARSE
//   (DRY — regex đã có test riêng), phần so sánh tự viết đủ §11.
//
// Không thêm dependency (`semver` npm) — bất biến của ticket #191.

import { parseSemVerTag } from "./pick-latest-stable-semver-tag.js";

// `ParsedSemVer` không được export ở module gốc ⇒ suy ra kiểu từ chính hàm parse,
// tránh phải sửa file thứ 3 chỉ để lấy 1 interface.
type Parsed = NonNullable<ReturnType<typeof parseSemVerTag>>;

export type PackVersionTransition = "upgrade" | "noop" | "downgrade" | "unknown";

// So 2 chuỗi prerelease theo SemVer 2.0.0 §11:
//   - stable (null) > prerelease
//   - identifier toàn số so bằng SỐ (beta.10 > beta.2, KHÔNG lexical)
//   - identifier số < identifier chữ
//   - còn lại so lexical ASCII
//   - tập ÍT field < tập NHIỀU field khi phần đầu bằng nhau (beta < beta.0)
function comparePrereleaseIdentifiers(a: string | null, b: string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;

  const left = a.split(".");
  const right = b.split(".");
  const length = Math.max(left.length, right.length);

  for (let i = 0; i < length; i++) {
    const x = left[i];
    const y = right[i];
    if (x === undefined) return -1;
    if (y === undefined) return 1;

    const xIsNumeric = /^\d+$/.test(x);
    const yIsNumeric = /^\d+$/.test(y);
    if (xIsNumeric && yIsNumeric) {
      const diff = Number(x) - Number(y);
      if (diff !== 0) return diff < 0 ? -1 : 1;
      continue;
    }
    if (xIsNumeric !== yIsNumeric) return xIsNumeric ? -1 : 1;
    if (x !== y) return x < y ? -1 : 1;
  }

  return 0;
}

// So đầy đủ 2 version đã parse. Trả -1 / 0 / 1.
function comparePackSemVer(a: Parsed, b: Parsed): number {
  if (a.major !== b.major) return a.major < b.major ? -1 : 1;
  if (a.minor !== b.minor) return a.minor < b.minor ? -1 : 1;
  if (a.patch !== b.patch) return a.patch < b.patch ? -1 : 1;
  return comparePrereleaseIdentifiers(a.prerelease, b.prerelease);
}

// Parse fail-soft: mọi input không phải chuỗi SemVer đều thành null (⇒ "unknown").
// Trim vì `.pack-version` có thể còn `\n` cuối file, và regex gốc neo `^…$` sẽ
// từ chối chuỗi có newline.
function parsePackVersion(value: string | null | undefined): Parsed | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return parseSemVerTag(trimmed);
}

// Phân loại chuyển version: bản ĐANG CÀI (`current`) → bản SẮP CÀI (`target`).
//
// `current` lớn hơn `target` ⇒ "downgrade" (đây là ca #191 cần cảnh báo).
// Bất kỳ phía nào không đọc được (chưa cài, `local:/path`, `local-2026-08-11`,
// `+build`, rỗng, null) ⇒ "unknown" — caller phải IM LẶNG, không đoán.
export function classifyPackVersionTransition(
  current: string | null | undefined,
  target: string | null | undefined,
): PackVersionTransition {
  const parsedCurrent = parsePackVersion(current);
  const parsedTarget = parsePackVersion(target);
  if (parsedCurrent === null || parsedTarget === null) return "unknown";

  const comparison = comparePackSemVer(parsedCurrent, parsedTarget);
  if (comparison === 0) return "noop";
  return comparison < 0 ? "upgrade" : "downgrade";
}
